import {
  FETCH_TEAMS, FETCH_TEAMS_SUCCESS, FETCH_TEAMS_FAIL,
  FETCH_TEAM, FETCH_TEAM_SUCCESS, FETCH_TEAM_FAIL, CLEAR_SELECTED_TEAM,
  CREATE_TEAM, CREATE_TEAM_SUCCESS, CREATE_TEAM_FAIL,
  UPDATE_TEAM, UPDATE_TEAM_SUCCESS, UPDATE_TEAM_FAIL,
  DELETE_TEAM, DELETE_TEAM_SUCCESS, DELETE_TEAM_FAIL,
  ADD_TEAM_MEMBER, ADD_TEAM_MEMBER_SUCCESS, ADD_TEAM_MEMBER_FAIL,
  REMOVE_TEAM_MEMBER, REMOVE_TEAM_MEMBER_SUCCESS, REMOVE_TEAM_MEMBER_FAIL,
  UPDATE_TEAM_MEMBER_ROLE, UPDATE_TEAM_MEMBER_ROLE_SUCCESS, UPDATE_TEAM_MEMBER_ROLE_FAIL,
  FETCH_TEAM_RESPONSIBILITIES, FETCH_TEAM_RESPONSIBILITIES_SUCCESS, FETCH_TEAM_RESPONSIBILITIES_FAIL,
  ATTACH_TEAM_RESPONSIBILITY, ATTACH_TEAM_RESPONSIBILITY_SUCCESS, ATTACH_TEAM_RESPONSIBILITY_FAIL,
  ATTACH_TEAM_RESPONSIBILITIES_BULK, ATTACH_TEAM_RESPONSIBILITIES_BULK_SUCCESS, ATTACH_TEAM_RESPONSIBILITIES_BULK_FAIL,
  DETACH_TEAM_RESPONSIBILITY, DETACH_TEAM_RESPONSIBILITY_SUCCESS, DETACH_TEAM_RESPONSIBILITY_FAIL,
  CLEAR_TEAM_MESSAGES,
} from './actionTypes';

const INIT_STATE = {
  teams: [],
  teamsLoading: false,
  teamsError: null,

  selectedTeam: null,
  selectedTeamLoading: false,
  selectedTeamError: null,

  saving: false,
  saveError: null,
  saveSuccess: null,

  memberSaving: false,
  memberError: null,
  memberSuccess: null,

  responsibilities: { locations: [], assets: [], assetTypes: [], workOrderCategories: [] },
  responsibilitiesLoading: false,
  responsibilitiesError: null,
  responsibilitySaving: false,
  responsibilityError: null,
  responsibilitySuccess: null,
};

const upsertMember = (members = [], member) => {
  const idx = members.findIndex((m) => m.userId === member.userId);
  if (idx === -1) return [...members, member];
  const next = members.slice();
  next[idx] = member;
  return next;
};

const Team = (state = INIT_STATE, action) => {
  switch (action.type) {
    // List
    case FETCH_TEAMS:
      return { ...state, teamsLoading: true, teamsError: null };
    case FETCH_TEAMS_SUCCESS:
      return { ...state, teams: action.payload || [], teamsLoading: false };
    case FETCH_TEAMS_FAIL:
      return { ...state, teamsLoading: false, teamsError: action.payload };

    // Detail
    case FETCH_TEAM:
      return { ...state, selectedTeamLoading: true, selectedTeamError: null };
    case FETCH_TEAM_SUCCESS:
      return { ...state, selectedTeam: action.payload, selectedTeamLoading: false };
    case FETCH_TEAM_FAIL:
      return { ...state, selectedTeamLoading: false, selectedTeamError: action.payload };
    case CLEAR_SELECTED_TEAM:
      return { ...state, selectedTeam: null, selectedTeamError: null };

    // Create
    case CREATE_TEAM:
      return { ...state, saving: true, saveError: null, saveSuccess: null };
    case CREATE_TEAM_SUCCESS: {
      const newTeam = action.payload;
      const summary = {
        id: newTeam.id,
        organizationId: newTeam.organizationId,
        name: newTeam.name,
        description: newTeam.description,
        photoUrl: newTeam.photoUrl,
        color: newTeam.color,
        memberCount: newTeam.memberCount ?? (newTeam.members ? newTeam.members.length : 0),
        createdAt: newTeam.createdAt,
        updatedAt: newTeam.updatedAt,
      };
      return {
        ...state,
        saving: false,
        saveSuccess: 'Team created successfully',
        teams: [summary, ...state.teams],
        selectedTeam: newTeam,
      };
    }
    case CREATE_TEAM_FAIL:
      return { ...state, saving: false, saveError: action.payload };

    // Update
    case UPDATE_TEAM:
      return { ...state, saving: true, saveError: null, saveSuccess: null };
    case UPDATE_TEAM_SUCCESS: {
      const t = action.payload;
      return {
        ...state,
        saving: false,
        saveSuccess: 'Team updated successfully',
        teams: state.teams.map((x) => (x.id === t.id ? { ...x, ...t, memberCount: t.memberCount ?? x.memberCount } : x)),
        selectedTeam: state.selectedTeam && state.selectedTeam.id === t.id
          ? { ...state.selectedTeam, ...t }
          : state.selectedTeam,
      };
    }
    case UPDATE_TEAM_FAIL:
      return { ...state, saving: false, saveError: action.payload };

    // Delete
    case DELETE_TEAM:
      return { ...state, saving: true, saveError: null, saveSuccess: null };
    case DELETE_TEAM_SUCCESS:
      return {
        ...state,
        saving: false,
        saveSuccess: 'Team deleted successfully',
        teams: state.teams.filter((x) => x.id !== action.payload),
        selectedTeam: state.selectedTeam && state.selectedTeam.id === action.payload ? null : state.selectedTeam,
      };
    case DELETE_TEAM_FAIL:
      return { ...state, saving: false, saveError: action.payload };

    // Add member
    case ADD_TEAM_MEMBER:
      return { ...state, memberSaving: true, memberError: null, memberSuccess: null };
    case ADD_TEAM_MEMBER_SUCCESS: {
      const member = action.payload;
      if (!state.selectedTeam) {
        return { ...state, memberSaving: false, memberSuccess: 'Member added' };
      }
      const updatedMembers = upsertMember(state.selectedTeam.members, member);
      return {
        ...state,
        memberSaving: false,
        memberSuccess: 'Member added',
        selectedTeam: { ...state.selectedTeam, members: updatedMembers, memberCount: updatedMembers.length },
        teams: state.teams.map((t) => (t.id === state.selectedTeam.id ? { ...t, memberCount: updatedMembers.length } : t)),
      };
    }
    case ADD_TEAM_MEMBER_FAIL:
      return { ...state, memberSaving: false, memberError: action.payload };

    // Remove member
    case REMOVE_TEAM_MEMBER:
      return { ...state, memberSaving: true, memberError: null, memberSuccess: null };
    case REMOVE_TEAM_MEMBER_SUCCESS: {
      const removedUserId = action.payload;
      if (!state.selectedTeam) {
        return { ...state, memberSaving: false, memberSuccess: 'Member removed' };
      }
      const remaining = (state.selectedTeam.members || []).filter((m) => m.userId !== removedUserId);
      return {
        ...state,
        memberSaving: false,
        memberSuccess: 'Member removed',
        selectedTeam: { ...state.selectedTeam, members: remaining, memberCount: remaining.length },
        teams: state.teams.map((t) => (t.id === state.selectedTeam.id ? { ...t, memberCount: remaining.length } : t)),
      };
    }
    case REMOVE_TEAM_MEMBER_FAIL:
      return { ...state, memberSaving: false, memberError: action.payload };

    // Update role
    case UPDATE_TEAM_MEMBER_ROLE:
      return { ...state, memberSaving: true, memberError: null, memberSuccess: null };
    case UPDATE_TEAM_MEMBER_ROLE_SUCCESS: {
      const member = action.payload;
      if (!state.selectedTeam) {
        return { ...state, memberSaving: false, memberSuccess: 'Role updated' };
      }
      return {
        ...state,
        memberSaving: false,
        memberSuccess: 'Role updated',
        selectedTeam: {
          ...state.selectedTeam,
          members: upsertMember(state.selectedTeam.members, member),
        },
      };
    }
    case UPDATE_TEAM_MEMBER_ROLE_FAIL:
      return { ...state, memberSaving: false, memberError: action.payload };

    // Responsibilities — fetch
    case FETCH_TEAM_RESPONSIBILITIES:
      return {
        ...state,
        responsibilitiesLoading: true,
        responsibilitiesError: null,
      };
    case FETCH_TEAM_RESPONSIBILITIES_SUCCESS: {
      const r = action.payload || {};
      return {
        ...state,
        responsibilitiesLoading: false,
        responsibilities: {
          locations: r.locations || [],
          assets: r.assets || [],
          assetTypes: r.assetTypes || [],
          workOrderCategories: r.workOrderCategories || [],
        },
      };
    }
    case FETCH_TEAM_RESPONSIBILITIES_FAIL:
      return {
        ...state,
        responsibilitiesLoading: false,
        responsibilitiesError: action.payload,
      };

    // Responsibilities — attach
    case ATTACH_TEAM_RESPONSIBILITY:
      return {
        ...state,
        responsibilitySaving: true,
        responsibilityError: null,
        responsibilitySuccess: null,
      };
    case ATTACH_TEAM_RESPONSIBILITY_SUCCESS: {
      const item = action.payload;
      const target = item.targetType;
      const list =
        target === 'LOCATION'
          ? 'locations'
          : target === 'ASSET'
            ? 'assets'
            : target === 'ASSET_TYPE'
              ? 'assetTypes'
              : 'workOrderCategories';
      const current = state.responsibilities[list] || [];
      const exists = current.some((x) => x.targetId === item.targetId);
      const next = exists ? current : [...current, item];
      next.sort((a, b) =>
        (a.targetName || '').toLowerCase().localeCompare(
          (b.targetName || '').toLowerCase()
        )
      );
      return {
        ...state,
        responsibilitySaving: false,
        responsibilitySuccess: 'Resource attached',
        responsibilities: { ...state.responsibilities, [list]: next },
      };
    }
    case ATTACH_TEAM_RESPONSIBILITY_FAIL:
      return {
        ...state,
        responsibilitySaving: false,
        responsibilityError: action.payload,
      };

    // Responsibilities — bulk attach
    case ATTACH_TEAM_RESPONSIBILITIES_BULK:
      return {
        ...state,
        responsibilitySaving: true,
        responsibilityError: null,
        responsibilitySuccess: null,
      };
    case ATTACH_TEAM_RESPONSIBILITIES_BULK_SUCCESS: {
      const payload = action.payload || {};
      const created = payload.created || [];
      if (created.length === 0) {
        return {
          ...state,
          responsibilitySaving: false,
          responsibilitySuccess: 'No new resources to attach',
        };
      }
      const target = payload.targetType || created[0].targetType;
      const list =
        target === 'LOCATION'
          ? 'locations'
          : target === 'ASSET'
            ? 'assets'
            : target === 'ASSET_TYPE'
              ? 'assetTypes'
              : 'workOrderCategories';
      const current = state.responsibilities[list] || [];
      const seen = new Set(current.map((x) => x.targetId));
      const next = current.slice();
      for (const item of created) {
        if (!seen.has(item.targetId)) {
          next.push(item);
          seen.add(item.targetId);
        }
      }
      next.sort((a, b) =>
        (a.targetName || '').toLowerCase().localeCompare(
          (b.targetName || '').toLowerCase()
        )
      );
      return {
        ...state,
        responsibilitySaving: false,
        responsibilitySuccess: 'Resources attached',
        responsibilities: { ...state.responsibilities, [list]: next },
      };
    }
    case ATTACH_TEAM_RESPONSIBILITIES_BULK_FAIL:
      return {
        ...state,
        responsibilitySaving: false,
        responsibilityError: action.payload,
      };

    // Responsibilities — detach
    case DETACH_TEAM_RESPONSIBILITY:
      return {
        ...state,
        responsibilitySaving: true,
        responsibilityError: null,
        responsibilitySuccess: null,
      };
    case DETACH_TEAM_RESPONSIBILITY_SUCCESS: {
      const { targetType, targetId } = action.payload || {};
      const list =
        targetType === 'LOCATION'
          ? 'locations'
          : targetType === 'ASSET'
            ? 'assets'
            : targetType === 'ASSET_TYPE'
              ? 'assetTypes'
              : 'workOrderCategories';
      const current = state.responsibilities[list] || [];
      return {
        ...state,
        responsibilitySaving: false,
        responsibilitySuccess: 'Resource detached',
        responsibilities: {
          ...state.responsibilities,
          [list]: current.filter((x) => x.targetId !== targetId),
        },
      };
    }
    case DETACH_TEAM_RESPONSIBILITY_FAIL:
      return {
        ...state,
        responsibilitySaving: false,
        responsibilityError: action.payload,
      };

    // Clear
    case CLEAR_TEAM_MESSAGES:
      return {
        ...state,
        saveError: null,
        saveSuccess: null,
        memberError: null,
        memberSuccess: null,
        responsibilityError: null,
        responsibilitySuccess: null,
      };

    default:
      return state;
  }
};

export default Team;
